import React from "react";

/**
 * The Workstation's own presentational parts.
 *
 * IN THIS FOLDER, NOT IN `shell/components/`
 * ==========================================
 * `shell/components/**` is frozen tonight with no owner, and the brief is
 * explicit: a shared shell component this area needs goes in the area's own
 * folder. If a second area later wants a KPI tile, promoting one from here is a
 * move; adding one to a frozen directory tonight would be an edit nobody owns.
 *
 * EVERY ONE OF THESE RENDERS ABSENCE AS ABSENCE
 * =============================================
 * R6 cuts both ways, as the corrected §4 says. A metric with no value renders
 * an em-dash and a reason, never a zero — "no rows in this window" and "zero
 * spend" are different facts, and a component that prints 0 for both destroys
 * the difference before a human ever sees it.
 */

const NO_VALUE = "—";

/** A number, or an honest dash. Never a substituted zero. */
export function Metric({ value, format = "number", currency = "$" }) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return (
      <span className="ws-metric ws-metric-absent" title="no value in this window">
        {NO_VALUE}
      </span>
    );
  }
  let text;
  if (format === "currency") {
    text = `${currency}${Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  } else if (format === "percent") {
    text = `${Number(value).toFixed(2)}%`;
  } else if (format === "ratio") {
    text = `${Number(value).toFixed(2)}×`;
  } else {
    text = Number(value).toLocaleString();
  }
  return <span className="ws-metric">{text}</span>;
}

/** One KPI. `note` is where a caveat about the number belongs. */
export function Kpi({ label, value, format, note }) {
  return (
    <div className="ws-kpi">
      <div className="ws-kpi-label">{label}</div>
      <div className="ws-kpi-value">
        <Metric value={value} format={format} />
      </div>
      {note ? <div className="ws-kpi-note dim">{note}</div> : null}
    </div>
  );
}

/** A titled block. `dominant` makes it the visual centre of its grid. */
export function Panel({ title, subtitle, dominant = false, actions, children }) {
  return (
    <section
      className={dominant ? "ws-panel ws-panel-dominant" : "ws-panel"}
      data-dominant={dominant ? "true" : "false"}
    >
      <header className="ws-panel-head">
        <div>
          <h2 className="ws-panel-title">{title}</h2>
          {subtitle ? <p className="ws-panel-sub dim">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ws-panel-actions">{actions}</div> : null}
      </header>
      <div className="ws-panel-body">{children}</div>
    </section>
  );
}

/**
 * The four states every endpoint-backed panel can be in.
 *
 * `error` distinguishes 403 from everything else, because a founder-gated
 * endpoint refusing a non-founder is CORRECT BEHAVIOUR and reporting it as a
 * failure would teach the operator to distrust a working surface.
 */
export function Async({ result, empty, children, deniedLabel }) {
  if (result.state === "idle") return null;
  if (result.state === "loading") {
    return (
      <div className="ws-loading" role="status" aria-live="polite">
        <span className="skeleton ws-skeleton-line" />
        <span className="skeleton ws-skeleton-line" />
      </div>
    );
  }
  if (result.state === "error") {
    if (result.status === 403) {
      return (
        <div className="state state-denied ws-state" role="status">
          {deniedLabel ||
            "This view is founder-only, and the server refused it. That is the gate working, not a fault."}
        </div>
      );
    }
    return (
      <div className="state state-error ws-state" role="status">
        Could not read this: {result.error}
      </div>
    );
  }
  const isEmpty =
    result.data === null ||
    result.data === undefined ||
    (Array.isArray(result.data) && result.data.length === 0);
  if (isEmpty && empty) {
    return (
      <div className="state ws-state" role="status">
        {empty}
      </div>
    );
  }
  return children(result.data);
}

/** A row of KPI tiles. */
export function KpiRow({ children }) {
  return <div className="ws-kpi-row">{children}</div>;
}
